import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { credentialStatusLabel } from "../../platform/locale";
import { CenterWorkflowActions } from "./CenterWorkflowActions";
import { CenterWalletClaimPanel } from "./CenterWalletClaimPanel";
import { CenterAssessmentPanel } from "./CenterAssessmentPanel";
import { CenterBadgeEmailPanel } from "./CenterBadgeEmailPanel";
import { StudentCareerCase360Panel } from "./StudentCareerCase360Panel";
import { CenterStudentRecordAiPanel } from "./CenterAiWriting";

type LearnerRow = {
  learner_id: string;
  display_name: string;
  enrollment_id: string;
  grade_level?: string | null;
  class_label?: string | null;
  journey_id?: string | null;
  journey_status?: string | null;
  participation_count: number;
  evidence_count: number;
  verified_evidence_count: number;
  evaluation_count: number;
  achievement_count: number;
  credential_count: number;
  last_activity_at?: string | null;
};

type StudentERP360 = {
  currentEnrollment?: {
    enrollmentId:string;
    schoolId:string;
    schoolName:string;
    academicYearId:string;
    yearCode:string;
    gradeLevel?:string|null;
    classLabel?:string|null;
    classGroupId?:string|null;
    className?:string|null;
    status:string;
    enrolledOn?:string|null;
  }|null;
  enrollmentHistory:Array<{
    enrollmentId:string;
    schoolId:string;
    schoolName:string;
    yearCode:string;
    gradeLevel?:string|null;
    classLabel?:string|null;
    className?:string|null;
    status:string;
    enrolledOn?:string|null;
    endedOn?:string|null;
  }>;
  classStaff:Array<{
    assignmentId:string;
    personId:string;
    displayName:string;
    roleType:string;
  }>;
  lifecycleEvents:Array<{
    eventId:string;
    eventType:string;
    eventDate:string;
    schoolId:string;
    schoolName:string;
    fromGradeLevel?:string|null;
    toGradeLevel?:string|null;
    fromClassLabel?:string|null;
    toClassLabel?:string|null;
    relatedSchoolName?:string|null;
    reason?:string|null;
  }>;
  assessmentSummary:{
    assignedCount:number;
    completedCount:number;
    lastCompletedAt?:string|null;
  };
  counselingSummary:{
    openCaseCount:number;
    sessionCount:number;
    lastSessionAt?:string|null;
    nextFollowUpAt?:string|null;
    recentSessions:Array<{
      sessionId:string;
      caseId:string;
      caseType:string;
      priority:string;
      topic:string;
      notes?:string|null;
      nextAction?:string|null;
      nextFollowUpAt?:string|null;
      counselorName:string;
      sessionAt:string;
    }>;
  };
  badgeSummary:{
    totalCount:number;
    activeCount:number;
    revokedCount:number;
    items:Array<{
      credentialId:string;
      credentialName:string;
      status:string;
      issuedAt:string;
      verificationUri?:string|null;
      issuingSchoolName?:string|null;
      yearCodeAtIssue?:string|null;
      gradeLevelAtIssue?:string|null;
      classLabelAtIssue?:string|null;
    }>;
  };
};

type LearnerCase = {
  learner: {
    learnerId: string;
    displayName: string;
    status: string;
    enrollmentId: string;
    gradeLevel?: string | null;
    classLabel?: string | null;
    schoolId: string;
  };
  journey?: {
    journeyId: string;
    status: string;
    startedAt: string;
    closedAt?: string | null;
  } | null;
  participations: Array<{
    participationId: string;
    status: string;
    registeredAt?: string | null;
    attendedAt?: string | null;
    completedAt?: string | null;
    activityId: string;
    activityTitle: string;
    activityType: string;
    programId: string;
    programTitle: string;
    sessionStartsAt?: string | null;
    sessionEndsAt?: string | null;
    locationName?: string | null;
  }>;
  evidence: Array<{
    evidenceId: string;
    title: string;
    type: string;
    status: string;
    visibility: string;
    verifiedAt?: string | null;
    createdAt: string;
    originType?: string | null;
    originResourceId?: string | null;
  }>;
  evaluations: Array<{
    evaluationResultId: string;
    definitionId: string;
    definitionName: string;
    status: string;
    numericScore?: number | null;
    normalizedScore?: number | null;
    observedAt: string;
    confirmedAt?: string | null;
  }>;
  achievements: Array<{
    achievementId: string;
    definitionName: string;
    status: string;
    periodKey: string;
    confirmedAt?: string | null;
  }>;
  credentials: Array<{
    credentialId: string;
    credentialName: string;
    status: string;
    issuedAt: string;
    expiresAt?: string | null;
    verificationUri?: string | null;
    externalCredentialId: string;
  }>;
};

function statusLabel(status?: string | null): string {
  if (!status) return "없음";
  const map: Record<string,string> = {
    ACTIVE:"활성",
    COMPLETED:"완료",
    ATTENDED:"참석",
    REGISTERED:"등록",
    VERIFIED:"검증 완료",
    SUBMITTED:"제출",
    DRAFT:"작성 중",
    CONFIRMED:"확인 완료",
    CANDIDATE:"성취 후보",
    REVOKED:"취소",
    EXPIRED:"만료"
  };
  return map[status] || status;
}

function formatDate(value?: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleString("ko-KR", {
    year:"numeric",
    month:"2-digit",
    day:"2-digit",
    hour:"2-digit",
    minute:"2-digit"
  });
}

export function CenterLearnersPage({
  client,
  schoolId,
  canCreateLearner
}: {
  client: PlatformClient;
  schoolId: string;
  canCreateLearner: boolean;
}) {
  const [learners,setLearners] = useState<LearnerRow[]>([]);
  const [selectedId,setSelectedId] = useState("");
  const [caseData,setCaseData] = useState<LearnerCase | null>(null);
  const [erp360,setErp360] = useState<StudentERP360 | null>(null);
  const [loading,setLoading] = useState(true);
  const [detailLoading,setDetailLoading] = useState(false);
  const [error,setError] = useState("");
  const [refreshKey,setRefreshKey] = useState(0);
  const [showCreate,setShowCreate] = useState(false);
  const [query,setQuery] = useState("");
  const [gradeFilter,setGradeFilter] = useState("전체");
  const [classFilter,setClassFilter] = useState("전체");

  useEffect(()=>{
    setSelectedId("");
    setCaseData(null);
    setErp360(null);
    setShowCreate(false);
  },[schoolId]);

  useEffect(() => {
    let active=true;
    setLoading(true);
    setError("");

    (async()=>{
      const {data,error} = await client.rpc("center_learners",{p_school_id:schoolId});
      if(!active) return;
      if(error){
        setError(error.message);
        setLoading(false);
        return;
      }

      const rows=(data || []) as LearnerRow[];
      setLearners(rows);
      const requested=new URLSearchParams(window.location.search).get("student") || "";
      setSelectedId((current)=>
        requested && rows.some((item)=>item.learner_id===requested)
          ? requested
          : rows.some((item)=>item.learner_id===current)
            ? current
            : rows[0]?.learner_id || ""
      );
      setLoading(false);
    })();

    return()=>{active=false;};
  },[client,schoolId,refreshKey]);

  useEffect(()=>{
    if(!selectedId) {
      setCaseData(null);
      return;
    }

    let active=true;
    setDetailLoading(true);
    setError("");

    (async()=>{
      const [caseResult,erpResult]=await Promise.all([
        client.rpc("center_learner_case",{
          p_school_id:schoolId,
          p_learner_id:selectedId
        }),
        client.rpc("center_student_erp_360",{
          p_school_id:schoolId,
          p_learner_id:selectedId
        })
      ]);
      if(!active) return;
      const firstError=caseResult.error || erpResult.error;
      if(firstError){
        setError(firstError.message);
        setCaseData(null);
        setErp360(null);
        setDetailLoading(false);
        return;
      }
      setCaseData(caseResult.data as LearnerCase);
      setErp360(erpResult.data as StudentERP360);
      setDetailLoading(false);
    })();

    return()=>{active=false;};
  },[client,schoolId,selectedId,refreshKey]);

  const selected=useMemo(
    ()=>learners.find((item)=>item.learner_id===selectedId) || null,
    [learners,selectedId]
  );

  const grades=useMemo(
    ()=>["전체",...Array.from(new Set(learners.map((item)=>item.grade_level).filter(Boolean) as string[])).sort((a,b)=>Number(a)-Number(b))],
    [learners]
  );

  const classes=useMemo(
    ()=>["전체",...Array.from(new Set(learners.map((item)=>item.class_label).filter(Boolean) as string[])).sort()],
    [learners]
  );

  const filteredLearners=useMemo(()=>{
    const q=query.trim().toLowerCase();
    return learners.filter((item)=>{
      if(gradeFilter!=="전체" && item.grade_level!==gradeFilter)return false;
      if(classFilter!=="전체" && item.class_label!==classFilter)return false;
      if(!q)return true;
      return [item.display_name,item.grade_level,item.class_label].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  },[learners,query,gradeFilter,classFilter]);

  function refresh(){
    setRefreshKey((value)=>value+1);
  }

  function handleCreated(learnerId:string){
    setShowCreate(false);
    setSelectedId(learnerId);
    refresh();
  }

  return (
    <section className="learner-workspace">
      <div className="section-heading">
        <div>
          <p className="eyebrow">학생</p>
          <h2>학생을 찾고, 다음 할 일을 바로 확인하세요</h2>
          <p className="route-description">이름·학년·반으로 찾고 검사, 활동, 배지까지 한 학생의 흐름에서 이어갑니다.</p>
        </div>
        <div className="section-actions">
          {canCreateLearner && (
            <button className="secondary" onClick={()=>setShowCreate((value)=>!value)}>
              {showCreate ? "등록 취소" : "학생 등록"}
            </button>
          )}

        </div>
      </div>

      {showCreate && canCreateLearner && (
        <CreateLearnerForm
          client={client}
          schoolId={schoolId}
          onCreated={handleCreated}
          onCancel={()=>setShowCreate(false)}
        />
      )}

      {error && <p className="error">{error}</p>}

      <div className="learner-layout">
        <aside className="learner-list-panel">
          <div className="panel-title">
            <strong>학생</strong>
            <span>{filteredLearners.length}/{learners.length}명</span>
          </div>

          <div className="learner-filter-box">
            <input
              className="teacher-search"
              value={query}
              onChange={(event)=>setQuery(event.target.value)}
              placeholder="학생 이름 검색"
            />
            <div className="learner-filter-selects">
              <select value={gradeFilter} onChange={(event)=>setGradeFilter(event.target.value)}>
                {grades.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 학년" : item+"학년"}</option>)}
              </select>
              <select value={classFilter} onChange={(event)=>setClassFilter(event.target.value)}>
                {classes.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 반" : item+"반"}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="muted">학생 목록을 불러오는 중…</p>
          ) : filteredLearners.length===0 ? (
            <div className="empty-inline">
              <strong>표시할 학생이 없습니다.</strong>
              <span>{canCreateLearner ? "학생 등록 버튼으로 첫 학생을 추가하세요." : "담당 학생 배정 상태를 확인하세요."}</span>
            </div>
          ) : (
            <div className="learner-list">
              {filteredLearners.map((learner)=>(
                <button
                  key={learner.learner_id}
                  className={selectedId===learner.learner_id ? "learner-row active" : "learner-row"}
                  onClick={()=>setSelectedId(learner.learner_id)}
                >
                  <div>
                    <strong>{learner.display_name}</strong>
                    <span>{learner.grade_level ? `${learner.grade_level}학년` : "학년 미정"}{learner.class_label ? ` · ${learner.class_label}반` : ""}</span>
                  </div>
                  <small>{statusLabel(learner.journey_status)}</small>
                </button>
              ))}
            </div>
          )}
        </aside>

        <div className="learner-detail-panel">
          {!selected ? (
            <div className="empty-inline large">
              <strong>학생을 선택하세요.</strong>
              <span>왼쪽 목록에서 확인할 학생을 선택합니다.</span>
            </div>
          ) : detailLoading ? (
            <p className="muted">학생 Case를 불러오는 중…</p>
          ) : caseData ? (
            <LearnerCaseView
              client={client}
              schoolId={schoolId}
              row={selected}
              data={caseData}
              erp360={erp360}
              canManageWalletClaim={canCreateLearner}
              onChanged={refresh}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function CreateLearnerForm({
  client,
  schoolId,
  onCreated,
  onCancel
}:{
  client:PlatformClient;
  schoolId:string;
  onCreated(learnerId:string):void;
  onCancel():void;
}){
  const [name,setName]=useState("");
  const [grade,setGrade]=useState("7");
  const [classLabel,setClassLabel]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");

  async function submit(event:React.FormEvent){
    event.preventDefault();
    setBusy(true);
    setError("");

    const {data,error}=await client.rpc("center_create_learner_case",{
      p_school_id:schoolId,
      p_display_name:name.trim(),
      p_grade_level:grade,
      p_class_label:classLabel.trim() || null
    });

    setBusy(false);

    if(error){
      setError(error.message);
      return;
    }

    const result=data as {learnerId:string};
    onCreated(result.learnerId);
  }

  return (
    <form className="create-learner-card" onSubmit={submit}>
      <div>
        <p className="eyebrow">새 학생</p>
        <h3>학생 등록과 진로성장 여정을 함께 시작합니다</h3>
      </div>
      <div className="form-grid">
        <label className="wide">
          학생 이름
          <input
            value={name}
            onChange={(event)=>setName(event.target.value)}
            placeholder="학생 이름"
            required
          />
        </label>
        <label>
          학년
          <select value={grade} onChange={(event)=>setGrade(event.target.value)}>
            {Array.from({length:12},(_,index)=>String(index+1)).map((value)=>(
              <option key={value} value={value}>{value}학년</option>
            ))}
          </select>
        </label>
        <label>
          반
          <input
            value={classLabel}
            onChange={(event)=>setClassLabel(event.target.value)}
            placeholder="예: 1"
          />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="form-actions">
        <button type="button" className="secondary" onClick={onCancel}>취소</button>
        <button className="primary compact-button" disabled={busy || !name.trim()}>
          {busy ? "등록 중…" : "학생 등록"}
        </button>
      </div>
    </form>
  );
}

function LearnerCaseView({
  client,
  schoolId,
  row,
  data,
  erp360,
  canManageWalletClaim,
  onChanged
}:{
  client:PlatformClient;
  schoolId:string;
  row:LearnerRow;
  data:LearnerCase;
  erp360:StudentERP360|null;
  canManageWalletClaim:boolean;
  onChanged():void;
}) {
  return (
    <>
      <header className="learner-case-header">
        <div>
          <p className="eyebrow">진로성장 Case</p>
          <h2>{data.learner.displayName}</h2>
          <p>{data.learner.gradeLevel ? `${data.learner.gradeLevel}학년` : "학년 미정"}{data.learner.classLabel ? ` · ${data.learner.classLabel}반` : ""}</p>
        </div>
        <span className="status-pill good">{statusLabel(data.journey?.status)}</span>
      </header>

      {erp360 && <StudentERP360Panel data={erp360} />}

      <CenterStudentRecordAiPanel client={client} schoolId={schoolId} learnerId={data.learner.learnerId} />

      <StudentCareerCase360Panel client={client} schoolId={schoolId} learnerId={data.learner.learnerId} />

      <div className="metric-grid">
        <Metric label="활동 참여" value={row.participation_count} />
        <Metric label="활동 증빙" value={row.evidence_count} suffix={row.verified_evidence_count ? ` · 검증 ${row.verified_evidence_count}` : ""} />
        <Metric label="평가 결과" value={row.evaluation_count} />
        <Metric label="성취" value={row.achievement_count} />
        <Metric label="자격증명" value={row.credential_count} />
      </div>

      <div className="student-quick-actions">
        <button onClick={()=>{window.history.pushState({},"","/student-lifecycle");window.dispatchEvent(new PopStateEvent("popstate"));}}>학적관리</button>
        <button onClick={()=>{window.history.pushState({},"",`/classes?tab=counseling&student=${encodeURIComponent(data.learner.learnerId)}`);window.dispatchEvent(new PopStateEvent("popstate"));}}>상담 기록</button>
        <button onClick={()=>{window.history.pushState({},"","/assessments");window.dispatchEvent(new PopStateEvent("popstate"));}}>진로검사 보내기</button>
        <button onClick={()=>{window.history.pushState({},"","/programs");window.dispatchEvent(new PopStateEvent("popstate"));}}>수업·활동 연결</button>
        <button onClick={()=>{window.history.pushState({},"",`/privacy?student=${encodeURIComponent(data.learner.learnerId)}`);window.dispatchEvent(new PopStateEvent("popstate"));}}>보호자 동의·공유</button>
        <button className="primary" onClick={()=>{window.history.pushState({},"","/badges");window.dispatchEvent(new PopStateEvent("popstate"));}}>배지 주기</button>
      </div>

      <details className="student-connect-details">
        <summary>학생 계정·배지 수령 설정</summary>
        <div className="student-connect-body">
          <CenterWalletClaimPanel
            client={client}
            schoolId={schoolId}
            learnerId={data.learner.learnerId}
            canManage={canManageWalletClaim}
          />
          <CenterBadgeEmailPanel
            client={client}
            schoolId={schoolId}
            learnerId={data.learner.learnerId}
            canManage={canManageWalletClaim}
          />
        </div>
      </details>

      <CenterAssessmentPanel
        client={client}
        schoolId={schoolId}
        learnerId={data.learner.learnerId}
        onChanged={onChanged}
      />

      <CenterWorkflowActions
        client={client}
        schoolId={schoolId}
        learnerId={data.learner.learnerId}
        hasCredential={data.credentials.some((item)=>item.status==="ACTIVE")}
        onChanged={onChanged}
      />

      <div className="case-sections">
        <CaseSection title="진로성장 여정">
          {data.journey ? (
            <div className="case-line">
              <div>
                <strong>활성 여정</strong>
                <span>시작 {formatDate(data.journey.startedAt)}</span>
              </div>
              <span className="status-pill">{statusLabel(data.journey.status)}</span>
            </div>
          ) : <EmptyText text="아직 시작된 진로성장 여정이 없습니다." />}
        </CaseSection>

        <CaseSection title="프로그램·활동">
          {data.participations.length ? data.participations.map((item)=>(
            <div className="case-line" key={item.participationId}>
              <div>
                <strong>{item.activityTitle}</strong>
                <span>{item.programTitle}</span>
                <small>{formatDate(item.sessionStartsAt)}{item.locationName ? ` · ${item.locationName}` : ""}</small>
              </div>
              <span className="status-pill">{statusLabel(item.status)}</span>
            </div>
          )) : <EmptyText text="활동 참여 기록이 없습니다." />}
        </CaseSection>

        <CaseSection title="활동 증빙">
          {data.evidence.length ? data.evidence.map((item)=>(
            <div className="case-line" key={item.evidenceId}>
              <div>
                <strong>{item.title}</strong>
                <span>{item.type}</span>
                <small>{item.verifiedAt ? `검증 ${formatDate(item.verifiedAt)}` : `등록 ${formatDate(item.createdAt)}`}</small>
              </div>
              <span className={item.status==="VERIFIED" ? "status-pill good" : "status-pill"}>{statusLabel(item.status)}</span>
            </div>
          )) : <EmptyText text="등록된 활동 증빙이 없습니다." />}
        </CaseSection>

        <CaseSection title="평가 결과">
          {data.evaluations.length ? data.evaluations.map((item)=>(
            <div className="case-line" key={item.evaluationResultId}>
              <div>
                <strong>{item.definitionName}</strong>
                <span>{item.normalizedScore ?? item.numericScore ?? "-"}점</span>
                <small>관찰 {formatDate(item.observedAt)}</small>
              </div>
              <span className={item.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>{statusLabel(item.status)}</span>
            </div>
          )) : <EmptyText text="평가 결과가 없습니다." />}
        </CaseSection>

        <CaseSection title="성취">
          {data.achievements.length ? data.achievements.map((item)=>(
            <div className="case-line" key={item.achievementId}>
              <div>
                <strong>{item.definitionName}</strong>
                <span>{item.periodKey}</span>
                <small>{item.confirmedAt ? `확인 ${formatDate(item.confirmedAt)}` : "확인 대기"}</small>
              </div>
              <span className={item.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>{statusLabel(item.status)}</span>
            </div>
          )) : <EmptyText text="확인된 성취가 없습니다." />}
        </CaseSection>

        <CaseSection title="디지털 자격증명">
          {data.credentials.length ? data.credentials.map((item)=>(
            <div className="case-line" key={item.credentialId}>
              <div>
                <strong>{item.credentialName}</strong>
                <span>발급 {formatDate(item.issuedAt)}</span>
                <small>{item.externalCredentialId}</small>
              </div>
              <div className="case-actions">
                <span className={item.status==="ACTIVE" ? "status-pill good" : "status-pill"}>
                  {credentialStatusLabel(item.status,item.status==="ACTIVE")}
                </span>
                {item.verificationUri && (
                  <a href={item.verificationUri} target="_blank" rel="noreferrer">검증</a>
                )}
              </div>
            </div>
          )) : <EmptyText text="발급된 자격증명이 없습니다." />}
        </CaseSection>
      </div>
    </>
  );
}

function StudentERP360Panel({data}:{data:StudentERP360}) {
  const roleMap:Record<string,string>={HOMEROOM:"담임",CAREER_TEACHER:"진로교사",COUNSELOR:"상담교사"};
  const enrollmentMap:Record<string,string>={ACTIVE:"재학",PLANNED:"예정",TRANSFERRED:"전출",COMPLETED:"수료",WITHDRAWN:"재학종료"};
  return (
    <section className="student-360">
      <div className="student-360-head">
        <div>
          <p className="eyebrow">Student 360</p>
          <strong>{data.currentEnrollment?.schoolName || "현재 학교"}</strong>
          <span>
            {data.currentEnrollment?.yearCode || "-"} · {data.currentEnrollment?.className || (
              data.currentEnrollment?.gradeLevel
                ? `${data.currentEnrollment.gradeLevel}학년 ${data.currentEnrollment.classLabel || ""}반`
                : "학급 미정"
            )}
          </span>
        </div>
        <div className="student-360-staff">
          {data.classStaff.map((staff)=>(
            <span key={staff.assignmentId}><b>{roleMap[staff.roleType] || staff.roleType}</b>{staff.displayName}</span>
          ))}
          {!data.classStaff.length && <small>학급 담당교사 미배정</small>}
        </div>
      </div>

      <div className="student-360-metrics">
        <Metric label="누적 학적" value={data.enrollmentHistory.length} />
        <Metric label="검사 완료" value={data.assessmentSummary.completedCount} suffix={` / ${data.assessmentSummary.assignedCount}`} />
        <Metric label="상담" value={data.counselingSummary.sessionCount} suffix={data.counselingSummary.openCaseCount ? ` · Case ${data.counselingSummary.openCaseCount}` : ""} />
        <Metric label="유효 배지" value={data.badgeSummary.activeCount} suffix={` / 누적 ${data.badgeSummary.totalCount}`} />
      </div>

      <div className="student-360-grid">
        <section>
          <div className="panel-title"><strong>학적 이력</strong><span>학교 이동 포함</span></div>
          {data.enrollmentHistory.slice(0,5).map((item)=>(
            <div className="student-360-line" key={item.enrollmentId}>
              <span><strong>{item.schoolName}</strong><small>{item.yearCode} · {item.gradeLevel || "-"}학년 {item.classLabel || ""}반</small></span>
              <b>{enrollmentMap[item.status] || item.status}</b>
            </div>
          ))}
        </section>
        <section>
          <div className="panel-title"><strong>최근 상담</strong><span>{data.counselingSummary.nextFollowUpAt ? `후속 ${formatDate(data.counselingSummary.nextFollowUpAt)}` : "후속 일정 없음"}</span></div>
          {data.counselingSummary.recentSessions.slice(0,4).map((item)=>(
            <div className="student-360-line" key={item.sessionId}>
              <span><strong>{item.topic}</strong><small>{item.counselorName} · {formatDate(item.sessionAt)}</small></span>
              <b>{item.priority==="HIGH" ? "우선" : item.priority==="ATTENTION" ? "관심" : "일반"}</b>
            </div>
          ))}
          {!data.counselingSummary.recentSessions.length && <p className="case-empty">현재 학교 상담기록이 없습니다.</p>}
        </section>
        <section className="wide">
          <div className="panel-title"><strong>누적 디지털배지</strong><span>전학 전 발급 포함</span></div>
          <div className="student-360-badges">
            {data.badgeSummary.items.slice(0,8).map((item)=>(
              <div key={item.credentialId}>
                <span><strong>{item.credentialName}</strong><small>{item.issuingSchoolName || "발급기관"} · {item.yearCodeAtIssue || "-"} {item.gradeLevelAtIssue ? `· ${item.gradeLevelAtIssue}학년 ${item.classLabelAtIssue || ""}반` : ""}</small></span>
                {item.verificationUri ? <a href={item.verificationUri} target="_blank" rel="noreferrer">{credentialStatusLabel(item.status,item.status==="ACTIVE")}</a> : <b>{credentialStatusLabel(item.status,item.status==="ACTIVE")}</b>}
              </div>
            ))}
            {!data.badgeSummary.items.length && <p className="case-empty">발급된 디지털배지가 없습니다.</p>}
          </div>
        </section>
      </div>
    </section>
  );
}

function Metric({label,value,suffix=""}:{label:string;value:number;suffix?:string}) {
  return (
    <div className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {suffix && <small>{suffix}</small>}
    </div>
  );
}

function CaseSection({title,children}:{title:string;children:React.ReactNode}) {
  return (
    <section className="case-section">
      <h3>{title}</h3>
      <div className="case-section-body">{children}</div>
    </section>
  );
}

function EmptyText({text}:{text:string}) {
  return <p className="case-empty">{text}</p>;
}
